// src/app/notifications/notification.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpService } from '../https.service';
import { URLUtils } from '../urlUtils';

export interface NavigationInfo {
  route_name: string;
  params?: {
    [key: string]: any;
    highlight_ids?: string[];
  };
}

export interface AppNotification {
  id: string;
  message: string;
  senderInitial: string;
  senderName: string;
  date: Date;
  displayDate: string;    // e.g. "Mar 24, 2025"
  displayTime: string;    // e.g. "02:45 PM"  ← NEW
  isRead: boolean;
  navigation?: NavigationInfo;
}

export interface NotificationGroup {
  label: string;
  sortKey: number;
  items: AppNotification[];
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notificationsSubject = new BehaviorSubject<AppNotification[]>([]);
  notifications$ = this.notificationsSubject.asObservable();

  constructor(private httpService: HttpService) {}

  get unreadCount(): number {
    return this.notificationsSubject.value.filter(n => !n.isRead).length;
  }

  get totalCount(): number {
    return this.notificationsSubject.value.length;
  }

  get groupedNotifications(): NotificationGroup[] {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const groups = new Map<string, NotificationGroup>();

    for (const notif of this.notificationsSubject.value) {
      const d = new Date(notif.date.getFullYear(), notif.date.getMonth(), notif.date.getDate());
      const diffDays = Math.round((today.getTime() - d.getTime()) / 86400000);

      let label: string;
      let sortKey: number;

      if (diffDays === 0) { label = 'Today'; sortKey = 0; }
      else if (diffDays === 1) { label = 'Yesterday'; sortKey = 1; }
      else if (diffDays < 30) { label = `${diffDays} days ago`; sortKey = diffDays; }
      else { label = this.formatGroupDate(notif.date); sortKey = diffDays; }

      if (!groups.has(label)) groups.set(label, { label, sortKey, items: [] });
      groups.get(label)!.items.push(notif);
    }

    // FIX 1: Sort groups latest first (ascending sortKey = recent first)
    const sortedGroups = Array.from(groups.values()).sort((a, b) => a.sortKey - b.sortKey);

    // FIX 1 (cont.): Within each group, sort items latest first by date
    sortedGroups.forEach(group => {
      group.items.sort((a, b) => b.date.getTime() - a.date.getTime());
    });

    return sortedGroups;
  }

  loadNotifications(): void {
    this.httpService.sendReGetRequest(URLUtils.getNotifications).subscribe({
      next: (res: any) => {
        const data: any[] = res?.notifications ?? res?.data ?? res ?? [];
        this.notificationsSubject.next(data.map(n => this.mapNotification(n)));
      },
      error: () => this.notificationsSubject.next([])
    });
  }

  markAsRead(ids: string[]): void {
    this.httpService.sendRePatchRequest(URLUtils.markNotificationsRead, { notification_ids: ids }).subscribe({
      next: () => this.updateReadState(ids),
      error: () => {} // do NOT update local state if API call fails — avoids reset on refresh
    });
  }

  deleteNotifications(ids: string[]): void {
    this.httpService.sendReDeleteRequestWithBody(URLUtils.deleteNotifications, { notification_ids: ids }).subscribe({
      next: () => this.removeFromList(ids),
      error: () => {} // do NOT remove locally if API call fails
    });
  }

  getAvatarColor(initial: string): string {
    const colors = [
      'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)',
      'linear-gradient(135deg, #198754 0%, #146c43 100%)',
      'linear-gradient(135deg, #6f42c1 0%, #59359a 100%)',
      'linear-gradient(135deg, #d63384 0%, #ab296a 100%)',
      'linear-gradient(135deg, #0dcaf0 0%, #0aa2c0 100%)',
      'linear-gradient(135deg, #fd7e14 0%, #ca6510 100%)',
    ];
    return colors[(initial.charCodeAt(0) || 0) % colors.length];
  }

  private updateReadState(ids: string[]): void {
    const updated = this.notificationsSubject.value.map(n =>
      ids.includes(n.id) ? { ...n, isRead: true } : n
    );
    this.notificationsSubject.next(updated);
  }

  private removeFromList(ids: string[]): void {
    this.notificationsSubject.next(
      this.notificationsSubject.value.filter(n => !ids.includes(n.id))
    );
  }

  private mapNotification(raw: any): AppNotification {
    const date = new Date(raw.timestamp || raw.created_at || raw.date);
    const senderName = raw.sender_name || raw.actor_name || raw.title || '';

    // FIX 2: Separate date and time strings
    const displayDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const displayTime = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

    return {
      id: String(raw.id || raw.notification_id || Math.random()),
      message: raw.message || raw.body || raw.description || '',
      senderInitial: senderName.trim().charAt(0).toUpperCase() || (raw.message || '?').trim().charAt(0).toUpperCase(),
      senderName,
      date,
      displayDate,
      displayTime,   // ← NEW field
      isRead: raw.status === 'read' || raw.is_read === true || raw.read === true,
      navigation: raw.navigation ? {
        route_name: raw.navigation.route_name,
        params: raw.navigation.params
      } : undefined
    };
  }

  private formatGroupDate(date: Date): string {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
}